import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { REHYDRATE } from 'redux-persist'
import type { StudioJobType, TranslatedCounts } from './studioTypes'
import type { StartParams } from './studioStartTypes'
import type { RootState } from '@/store'

export interface JobEntry {
  jobId: number
  type: StudioJobType
  /** Для мягкого баннера «идёт дольше обычного» — производной, а не таймера. */
  startedAt: number
  /** Джоба не возвращает параметры запуска (§1.2) — храним у себя. */
  params: StartParams | null
  /**
   * Число единиц (`targets_total`/`blocks_total`/`units_total`) из ответа
   * 201 на старт. До первого отчёта воркфлоу `job.result` ещё `null`, а это
   * поле уже есть — читает `useCourseJob` как `fallbackTotal` для
   * `readProgress`, чтобы не показывать «0 из —» первые секунды прогона.
   */
  total: number | null
  /**
   * Прогон — картиночная половина цепочки «перевод → картинки» (§7.4). Живёт
   * СВОИМ полем, а не выводится из `previous.type` (финальное ревью, п.3):
   * когда эта же джоба обрывается и её продолжают (§8), `useResumeJob`
   * перезаписывает `previous` целиком снимком картиночного родителя —
   * `previous.type === 'course_translation'` после этого гаснет, хотя
   * прогон всё ещё та же цепочка. Флаг нужен (§6.10) для правильной работы
   * UI шапки и меню языка: без него состояние прерванной цепочки при
   * перезагрузке потеряется. `useTranslationChain` ставит флаг первым,
   * `useResumeJob` ОБЯЗАН перенести его на новую запись.
   */
  translationChain?: true
  /**
   * Сколько картинок перевод обещал нарисовать — из сметы на старте, только
   * когда картинки входят в прогон. Нужно общей полосе (§6.4) до того, как
   * картиночная джоба назовёт своё `images_total`: без оценки полоса доходит
   * до 100% на конце текста и откатывается. Цепочка переносит поле на
   * картиночную запись. Опционально: записи из persist его не несут.
   */
  imagesTotal?: number
  /**
   * Снимок ПРЕДЫДУЩЕЙ джобы: у цепочки «перевод → картинки» (§7.4) и у
   * продолжения прерванного прогона (§8). Перецепив поллинг, мы теряем
   * терминальный `result` родителя — его числа и пропуски автор ещё не видел,
   * а взять их больше неоткуда.
   *
   * Храним УЖЕ ПОСЧИТАННЫЕ числа, а не сырой `result`: родителем бывает любой
   * из четырёх типов, и объединение результатов заставило бы плашку разбирать
   * его во второй раз.
   */
  previous?: {
    jobId: number
    type: StudioJobType
    /**
     * Явный повод снимка (финальное ревью, п.1) — НЕ выводить из наличия
     * `reason`: у родителя, завершившегося `success` с непустым
     * `result.skipped`, `reason` пуст (ошибки не было), а он всё равно
     * штатный кандидат на продолжение (§8 — пропущенные единицы
     * возвращаются в план). Вывод по `reason` молча показывал бы плашку
     * цепочки перевода поверх продолжения генерации курса.
     */
    kind: 'chain' | 'resume'
    progress: { done: number; total: number | null }
    skipped?: Record<string, string>
    /** Коды причин к `skipped` — без них снимок показал бы серверный текст вместо нашего. */
    skipped_codes?: Record<string, string>
    /** Итог перевода по видам (`result.translated`) — только у `kind: 'chain'`.
     *  Нужен итогу всей цепочки: джобы перевода в кеше к этому моменту нет.
     *  Опционально: снимки, лежащие в persist до этой версии, его не несут. */
    translated?: TranslatedCounts
    /** Почему прогон оборвался — только у `kind: 'resume'`, уже переведено
     *  на русский (`failedReasonText`); у цепочки перевода и у продолжения
     *  успешного родителя без ошибки — пусто. */
    reason?: string | null
  }
}

// Ключ — course_id, а не вкладка: серверный гейт «одна открытая джоба» тоже
// покурсовой, и «показать прогон» на карточке курса — тот же вопрос.
interface AiJobsState {
  byCourse: Record<number, JobEntry>
  /**
   * Курсы, чей прогон бэк больше не отдаёт (`404 job_not_found` /
   * `403 job_not_author`). Запись реестра к этому моменту удалена, а вместе с
   * ней — инвариантом `chatSlice` — и история чата: без отметки автор видит,
   * как его реплика и лента исчезают без единого слова (M-6).
   *
   * Поле опциональное не для удобства: `aiJobs` лежит в persist-whitelist, и у
   * состояния, сохранённого до этой версии, его нет вовсе — редьюсер persist
   * подменяет слайс целиком, а не сливает по ключам.
   */
  lostByCourse?: Record<number, true>
}

const initialState: AiJobsState = { byCourse: {}, lostByCourse: {} }

/** Отметка «прогон пропал» живёт до следующего действия автора по этому курсу. */
const clearLost = (state: AiJobsState, courseId: number) => {
  if (state.lostByCourse) delete state.lostByCourse[courseId]
}

/**
 * Санация одной записи при рехайдрации persist. Форма `previous` менялась
 * ДВАЖДЫ, и та, что реально лежит в localStorage у авторов сегодня — НЕ та,
 * что чистилась раньше:
 *
 * - прод-форма (`dev`/`3be656ef`, то, что у ВСЕХ в браузерах прямо сейчас):
 *   `{ jobId, type, result: TranslationResult }` — БЕЗ `progress`, БЕЗ
 *   `kind`;
 * - переходная форма (между коммитами `e94e26b1` и `d7250f30` этой же
 *   ветки): `{ jobId, type, progress, ... }` — `progress` уже есть, `kind`
 *   ещё нет. В проде её не было ни секунды — появиться она могла только в
 *   localStorage разработчика между двумя коммитами этой ветки.
 *
 * Числа прод-формы читать нельзя (`result`, а не `progress`) — `previous`
 * выбрасывается, как и раньше. НО `previous.type === 'course_translation'`
 * у неё тоже есть, и раньше именно из него читался гвард
 * `isStartedTranslationRun` (`entry?.previous?.type === 'course_translation'`,
 * `dev`/`3be656ef`) — сегодня он читает только `entry.translationChain` и
 * `previous` вовсе не смотрит. Значит признак НАДО восстановить ИЗ
 * `previous.type` ПЕРЕД тем, как выбросить сам `previous` — иначе автор, у
 * которого в реестре висит картиночная половина цепочки «перевод →
 * картинки», на первой же перезагрузке после выката теряет гвард: в шапке
 * «Языки» UI деградирует (§6.10). История: когда-то сервер отвечал `422
 * source_lang_locked` при такой ошибке, теперь поведение другое, но флаг
 * остался нужен для правильной работы UI.
 *
 * Переходную форму (`progress` уже верной формы, просто без `kind`) по-
 * прежнему достраиваем на месте — `kind: 'chain'` вместо выброса, признак
 * переносится тем же путём. Форму С `kind` не трогаем вовсе.
 */
const upgradeRehydratedEntry = (entry: JobEntry): JobEntry => {
  const previous = entry.previous
  if (!previous) return entry
  if (previous.kind) return entry

  // Признак — из type, а не из формы previous: он общий для прод-формы
  // (result) и переходной формы (progress), и должен выжить, даже когда
  // сам снимок ниже будет выброшен (прод-форма).
  const translationChain: true | undefined =
    previous.type === 'course_translation' ? true : entry.translationChain

  if (previous.progress && previous.type === 'course_translation') {
    return { ...entry, translationChain, previous: { ...previous, kind: 'chain' } }
  }
  return { ...entry, translationChain, previous: undefined }
}

export const aiJobsSlice = createSlice({
  name: 'aiJobs',
  initialState,
  reducers: {
    /**
     * `continuesChat` в payload сам по себе на реестр не влияет — это флаг
     * для `chatSlice` (единственное исключение из инварианта «перецепление
     * стирает историю», §7.4). Он живёт здесь, а не отдельным экшеном,
     * потому что оба слайса обязаны увидеть ОДНО и то же действие в одном
     * тике диспатча.
     */
    attachJob: (
      state,
      action: PayloadAction<{ courseId: number; entry: JobEntry; continuesChat?: boolean }>,
    ) => {
      state.byCourse[action.payload.courseId] = action.payload.entry
      clearLost(state, action.payload.courseId)
    },
    detachJob: (state, action: PayloadAction<number>) => {
      delete state.byCourse[action.payload]
      clearLost(state, action.payload)
    },
    /**
     * Прогон исчез на бэке. Не `detachJob`: отцепить запись мало — надо
     * объяснить, почему пропала лента. Диспатчит только `JobPoller`.
     */
    loseJob: (state, action: PayloadAction<number>) => {
      delete state.byCourse[action.payload]
      state.lostByCourse = { ...(state.lostByCourse ?? {}), [action.payload]: true }
    },
    dismissLostJob: (state, action: PayloadAction<number>) => {
      clearLost(state, action.payload)
    },
  },
  extraReducers: (builder) => {
    /**
     * Отметка «прогон пропал» эфемерна: она объясняет то, что произошло СЕЙЧАС.
     * Пережив перезагрузку, она всплыла бы через неделю вне контекста и до
     * «Понятно» закрывала бы собой экран настроек. Реестр джоб при этом обязан
     * переживать перезагрузку — ради него слайс и лежит в persist-whitelist.
     *
     * Ветка САМА применяет рехайдрацию слайса, а не просто чистит поле, и это
     * не перестраховка: `autoMergeLevel1` (реконсилятор по умолчанию) жёстко
     * перезаписывает подсостояние значением из хранилища — но ТОЛЬКО если
     * редьюсер его не менял (`originalState[key] !== reducedState[key]` →
     * skip). Поэтому «просто delete» здесь бесполезен вдвойне: на старте
     * приложения удалять ещё нечего (состояние — initialState), ссылка не
     * меняется, и отметка приезжает из localStorage следом. А любая мутация
     * ради изменения ссылки заставила бы реконсилятор пропустить ВЕСЬ слайс и
     * потерять реестр. Возвращаем санированное состояние — реконсилятор
     * уступает ему, и оба требования выполнены разом.
     */
    builder.addCase(REHYDRATE, (state, action) => {
      const inbound = (action as { payload?: { aiJobs?: AiJobsState } }).payload?.aiJobs
      // Ключа в хранилище нет — трогать нечего, и ссылку менять нельзя.
      if (!inbound) return state
      // Санация — см. upgradeRehydratedEntry: снимок без progress
      // выбрасывается, снимок без kind достраивается (цепочка перевода)
      // либо выбрасывается (любой другой прошлый повод).
      const byCourse = Object.fromEntries(
        Object.entries(inbound.byCourse ?? {}).map(([id, entry]) => [
          id,
          upgradeRehydratedEntry(entry),
        ]),
      )
      return { byCourse, lostByCourse: {} }
    })
  },
})

export const { attachJob, detachJob, loseJob, dismissLostJob } = aiJobsSlice.actions
export const aiJobsReducer = aiJobsSlice.reducer

export const selectJobByCourse = (state: RootState, courseId: number): JobEntry | undefined =>
  state.aiJobs.byCourse[courseId]

/** Весь реестр разом — для списка курсов: хук на курс там звать нельзя
 *  (хуки в цикле), а ссылка меняется только на attach/detach/lose, так что
 *  лишних перерисовок селектор не даёт. */
export const selectJobsByCourse = (state: RootState): Record<number, JobEntry> =>
  state.aiJobs.byCourse

/**
 * Запись реестра — прогон ПЕРЕВОДА (сам перевод или картиночная половина его
 * цепочки). Про то, идёт ли он, предикат НЕ говорит: терминальная запись
 * живёт в реестре бессрочно. «Идёт сейчас» — `useHasStartedTranslationRun`,
 * он добавляет гейт на опрос; список курсов опроса не ведёт и читает голый
 * предикат — там он лишь глушит подсказку «язык не объявлен».
 *
 * Запись цепочки (§7.4) к моменту картинок уже перецеплена — признак читает
 * `entry.translationChain`, а НЕ `entry.previous?.type` (финальное ревью,
 * п.3): продолжение прерванной картиночной половины (§8) перезаписывает
 * `previous` целиком снимком картиночного родителя, и вывод по
 * `previous.type` молча гас бы ровно на прерванной половине цепочки —
 * `useResumeJob` переносит явный флаг на новую запись отдельно.
 */
export const isStartedTranslationRun = (entry: JobEntry | undefined): boolean =>
  entry?.type === 'course_translation' || Boolean(entry?.translationChain)

export const selectJobLost = (state: RootState, courseId: number): boolean =>
  Boolean(state.aiJobs.lostByCourse?.[courseId])
